'use client';

import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

const GlobalRemindDialog = dynamic(() => import('@/components/global-remind-dialog'), { ssr: false });

const HIDE_REMIND_DIALOG_PATHNAMES = [
  '/cart',
  '/checkout',
  '/checkout/onepage/success',
  '/checkout/retry',
  '/checkout/pay-result',
  '/checkout/pay-cancel',
];
const events = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];
const REMIND_KEY = `${process.env.NEXT_PUBLIC_STORE_VIEW_CODE}-stopped-remind`;

const setStorageNotShowRemind = () => {
  const nowTimestamp = Math.floor(Date.now() / 1000);

  try {
    window.localStorage.setItem(REMIND_KEY, String(nowTimestamp));
  } catch (error) {
    console.error(error);
  }
};

const checkStorageShouldShowRemind = () => {
  let prevTimestamp: string | null = null;

  try {
    prevTimestamp = window.localStorage.getItem(REMIND_KEY);
  } catch (error) {
    console.error(error);
    return false;
  }

  if (!prevTimestamp) return true;

  const numericPrevTimestamp = parseInt(prevTimestamp, 10);
  const nowTimestamp = Math.floor(Date.now() / 1000);

  if (isNaN(numericPrevTimestamp) || nowTimestamp - numericPrevTimestamp > 12 * 60 * 60) {
    window.localStorage.removeItem(REMIND_KEY);
    return true;
  }

  return false;
};

let isLoaded = false;
const REMIND_SECONDS = 25;

const GlobalRemindDialogLazyLoad = () => {
  const pathname = usePathname();
  const [showDialog, setShowDialog] = useState(false);
  const mainTimerRef = useRef<NodeJS.Timeout | null>(null);
  const visibilityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimestampRef = useRef<number | null>(null);

  useEffect(() => {
    const checkShouldShowRemind = () =>
      !HIDE_REMIND_DIALOG_PATHNAMES.includes(pathname) && checkStorageShouldShowRemind();

    if (!checkShouldShowRemind()) {
      setShowDialog(false);
      return;
    }

    const stopMainTimer = () => {
      if (mainTimerRef.current) {
        clearTimeout(mainTimerRef.current);
        mainTimerRef.current = null;
      }
    };

    const stopVisibilityTimer = () => {
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
      }
    };

    const showDialog = () => {
      setTimeout(() => {
        const shouldShowRemind = checkStorageShouldShowRemind();
        setShowDialog(shouldShowRemind);
        isLoaded = shouldShowRemind;
        setStorageNotShowRemind();
      }, Math.random() * 2000);

      for (const event of events) {
        window.removeEventListener(event, startMainTimer);
      }
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
    };

    const startMainTimer = () => {
      stopMainTimer();
      stopVisibilityTimer();
      startTimestampRef.current = Math.floor(Date.now() / 1000);
      mainTimerRef.current = setTimeout(() => {
        showDialog();
        stopVisibilityTimer();
      }, REMIND_SECONDS * 1000);
    };

    const visibilityChangeHandler = () => {
      if (document.hidden) {
        stopMainTimer();
        stopVisibilityTimer();
        return;
      }

      const recordSeconds = startTimestampRef.current
        ? Math.floor(Date.now() / 1000) - startTimestampRef.current
        : null;

      if (recordSeconds === null) return;

      if (recordSeconds >= REMIND_SECONDS) {
        stopMainTimer();
        stopVisibilityTimer();
        startTimestampRef.current = null;
        showDialog();
        return;
      }

      stopVisibilityTimer();
      visibilityTimerRef.current = setTimeout(
        () => {
          stopMainTimer();
          showDialog();
        },
        (REMIND_SECONDS - recordSeconds) * 1000,
      );
    };

    startMainTimer();

    for (const event of events) {
      window.addEventListener(event, startMainTimer);
    }

    document.addEventListener('visibilitychange', visibilityChangeHandler);

    return () => {
      document.removeEventListener('visibilitychange', visibilityChangeHandler);

      for (const event of events) {
        window.removeEventListener(event, startMainTimer);
      }

      if (mainTimerRef.current) {
        clearTimeout(mainTimerRef.current);
        mainTimerRef.current = null;
      }

      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
      }
    };
  }, [pathname]);

  return (
    isLoaded &&
    !HIDE_REMIND_DIALOG_PATHNAMES.includes(pathname) && (
      <GlobalRemindDialog
        open={showDialog}
        onOpenChange={setShowDialog}
      />
    )
  );
};

export default GlobalRemindDialogLazyLoad;
